import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { controlToastState } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import {
  getFileDownloadUrl,
  getFileFromFirebaseStorage,
} from "../../../api/firebase/firestoreService";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { resetFileUploadState } from "../../../redux/slice/fileUploadSlice";
import { AppDispatch } from "../../../redux/store/store";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import { mergeDocuments } from "../../../utils/documentMerger";
import { downloadFile } from "../../../utils/fileDownloader";
import { uploadToStorageAndUpdateFirestore } from "../../../utils/fileUpload";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getPatientName } from "../utils/documentHelper";
import { generatePathForFileUpload } from "../utils/generatePathForFileUpload";

interface UseDocumentManagementProps {
  singleMedicalPaOrder: MedicalPaOrder | null;
  isLoadingSingleMedicalPaOrder: boolean;
}

export const useDocumentManagement = ({
  singleMedicalPaOrder,
  isLoadingSingleMedicalPaOrder,
}: UseDocumentManagementProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const uuid = useMemo(() => uuidv4(), []);

  const [patientName, setPatientName] = useState<string>("");
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);

  // Track previous order data to prevent unnecessary effect executions
  const previousOrderRef = useRef<MedicalPaOrder | null>(null);

  const uploadDraft = useCallback(
    async (mergedFile: Blob, orderId: string) => {
      const filePath = generatePathForFileUpload(orderId, uuid);

      await uploadToStorageAndUpdateFirestore({
        file: mergedFile,
        storagePath: filePath,
        fileName: `${uuid}.pdf`,
        collection: "medical_pa_orders",
        documentId: orderId,
        fieldPath: "clinical_document.file_path",
      });
    },
    [uuid],
  );

  const fetchDownloadUrl = useCallback(async (refUrl: string) => {
    setIsLoading(true);
    try {
      const downloadUrl = await getFileDownloadUrl(refUrl);
      setPdfUrl(downloadUrl);
      const downloadedDocument = await getFileFromFirebaseStorage(downloadUrl);
      setDocuments(downloadedDocument ? [downloadedDocument] : []);
    } catch (err) {
      logDataToConsole("Error fetching document", err);
      setError("Failed to load the document. Please try again later.");
      setPdfUrl("");
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const processOrderData = useCallback(
    (order: MedicalPaOrder) => {
      const name = getPatientName(order);
      setPatientName(name);

      if (location.state?.faxPdfLink) {
        fetchDownloadUrl(location.state.faxPdfLink);
        return;
      }

      if (order?.clinical_document?.file_path) {
        fetchDownloadUrl(order.clinical_document.file_path);
      }
    },
    [location.state?.faxPdfLink, fetchDownloadUrl],
  );

  useEffect(() => {
    if (singleMedicalPaOrder && !isLoadingSingleMedicalPaOrder) {
      const hasChanged = !isEqual(
        previousOrderRef.current,
        singleMedicalPaOrder,
      );

      if (hasChanged) {
        previousOrderRef.current = singleMedicalPaOrder;
        processOrderData(singleMedicalPaOrder);
      }
    }
  }, [singleMedicalPaOrder, isLoadingSingleMedicalPaOrder, processOrderData]);

  const downloadPdf = useCallback(async () => {
    setIsLoading(true);
    try {
      if (documents && documents.length > 0) {
        const mergedResult = await mergeDocuments(documents, patientName);

        if (!mergedResult) {
          setError("Failed to merge documents. Please try again.");
          controlToastState("download-failure");
          return;
        }

        await uploadDraft(
          mergedResult.mergedFile,
          singleMedicalPaOrder?.id ?? "",
        );

        dispatch(resetFileUploadState());

        await downloadFile({
          file: mergedResult.mergedFile,
          fileName: mergedResult.fileName,
          onSuccess: () => {
            controlToastState("download-success");

            const parentTable =
              getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE) ?? "";
            setTimeout(() => {
              navigate(`/worklists/${parentTable}`);
            }, 200);
          },
          onError: (error) => {
            setError("Failed to download the document. Please try again.");
            controlToastState("download-failure");
          },
        });
      }
    } catch (err) {
      logError(err as Error, "Error downloading PDF");
      setError("Failed to download the document. Please try again later.");
      controlToastState("download-failure");
    } finally {
      setIsLoading(false);
    }
  }, [
    documents,
    patientName,
    navigate,
    dispatch,
    uploadDraft,
    singleMedicalPaOrder?.id,
  ]);

  return {
    patientName,
    pdfUrl,
    isLoading,
    error,
    fileNames,
    documents,
    setFileNames,
    setDocuments,
    downloadPdf,
  };
};
